import createCache from "@emotion/cache"
import { CacheProvider } from "@emotion/react"
import type { Metadata } from "@infinitaslearning/module-il-feedback-widget"
import Feedback from "@infinitaslearning/module-il-feedback-widget"
import { Box, Typography } from "@material-ui/core"
import IconButton from "@material-ui/core/IconButton"
import FeedbackIcon from "@mui/icons-material/Announcement"
import type { FC } from "react"
import { useTranslation } from "react-i18next"
import { config } from "../../../config"
import { getBusinessUnit } from "../../../utils/business.utils"
import { Body1 } from "../../atoms"
import { useAuthenticatedUser } from "../../indentity-provider.component"

import "./content-feedback.styles.scss"

// The app runs @material-ui/core v4 (JSS) and @mui/material v5 (emotion) side
// by side. v4 appends JSS sheets to <head> as components mount, overriding v5
// rules for shared class names and breaking the feedback widget's layout.
// We give the widget its own emotion cache with a unique key so all v5 rules
// it emits are scoped to the widget subtree, and emotion inserts them at the
// end of <head> on first modal open — after any v4 JSS already mounted — so
// v5 wins the cascade inside the widget without touching the global cascade.
const feedbackEmotionCache = createCache({ key: "fb" })

export type FeedbackProps = {
  metadata: Metadata
}

export const ContentFeedback: FC<FeedbackProps> = ({ metadata }: FeedbackProps) => {
  const { t } = useTranslation("feedback")
  const user = useAuthenticatedUser()
  const businessUnit = getBusinessUnit()

  const collectMetadata = async (): Promise<Metadata> => {
    return { ...metadata }
  }

  return (
    <CacheProvider value={feedbackEmotionCache}>
      <Box className="content-feedback">
        <Feedback
          customButton={
            <IconButton color="primary">
              <FeedbackIcon sx={{ marginRight: 1 }} />
              <Typography variant="button">{t("button")}</Typography>
            </IconButton>
          }
          feedbackServiceUrl={config.feedbackServiceUrl}
          opco={businessUnit}
          segment="Secondary"
          userEmail={user.email}
          collectMetadata={collectMetadata}
          selectDisplayEmpty
          texts={{
            feedbackButtonText: t("button"),
            confirmationClose: t("confirmationClose"),
            confirmationMessage: t("confirmationMessage"),
            confirmationTitle: t("confirmationTitle"),
            sendFailureMessage: t("sendFailureMessage"),
            sendFailureTitle: t("sendFailureTitle"),
            feedbackModalTexts: {
              category: t("category"),
              feedbackTypes: [
                {
                  key: "Content",
                  text: t("contentIssue")
                },
                {
                  key: "Answer",
                  text: t("answerIssue")
                },
                {
                  key: "CantFinish",
                  text: t("cantFinish"),
                  component: <Body1>{t("technicalTicketExplanation")}</Body1>
                },
                {
                  key: "AnswerNotStored",
                  text: t("answerNotStored"),
                  component: <Body1>{t("technicalTicketExplanation")}</Body1>
                },
                {
                  key: "ContentNotLoading",
                  text: t("contentNotLoading"),
                  component: <Body1>{t("technicalTicketExplanation")}</Body1>
                },
                {
                  key: "Other",
                  text: t("otherIssue"),
                  component: (
                    <Body1>
                      {t("contactInformation1")}
                      <a href="https://www.noordhoff.nl/klantenservice"> noordhoff.nl/klantenservice </a>
                      {t("contactInformation2")}
                    </Body1>
                  ),
                  hideForm: true
                }
              ],
              optIn: t("optIn"),
              invalidEmail: t("invalidEmail"),
              describeError: t("describeError"),
              description: t("description"),
              pickCategory: t("pickCategory"),
              privacy: {
                policy1: t("policy1"),
                policy2: t("policy2"),
                policy3: t("policy3"),
                url: "https://www.noordhoff.nl/privacy"
              },
              confirmDialogTexts: {
                confirmationLabel: t("submit"),
                description: t("information"),
                title: t("header"),
                rejectLabel: t("cancel")
              }
            }
          }}
        />
      </Box>
    </CacheProvider>
  )
}
